import { useState, useEffect, useRef, useCallback } from 'react'
import { authFetch } from '../../lib/api'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'

interface StoredFile {
  id: string
  filename: string
  mimetype: string
  size: number
  created_at: string
  url: string
}

export function StoragePanel() {
  const project = usePulseMemory(s => s.memory.project) || 'default'
  const [files, setFiles] = useState<StoredFile[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authFetch(`/api/storage/list/${encodeURIComponent(project)}`)
      if (r.ok) setFiles(await r.json())
    } catch {}
    setLoading(false)
  }, [project])

  useEffect(() => { load() }, [load])

  const uploadFile = async (file: File) => {
    setUploading(true)
    const buf = await file.arrayBuffer()
    const data = btoa(String.fromCharCode(...new Uint8Array(buf)))
    try {
      const r = await authFetch('/api/storage/upload', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ project, filename: file.name, data, mimetype: file.type }),
      })
      if (r.ok) load()
    } catch {}
    setUploading(false)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false)
    for (const file of Array.from(e.dataTransfer.files)) uploadFile(file)
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this file?')) return
    await authFetch(`/api/storage/${id}`, { method: 'DELETE' })
    load()
  }

  const copyUrl = (url: string) => {
    navigator.clipboard.writeText(window.location.origin + url)
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B'
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB'
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB'
  }

  const isImage = (mime: string) => mime.startsWith('image/')

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const dropZone: React.CSSProperties = { border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`, borderRadius: 8, padding: 24, textAlign: 'center', marginBottom: 16, cursor: 'pointer', background: dragOver ? 'rgba(124,110,245,0.05)' : 'transparent', transition: 'all 0.2s' }

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>📁 App Storage</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>
        Upload images, files, assets for project: <strong>{project}</strong>
      </p>

      {/* Drop zone */}
      <div
        style={dropZone}
        onDragOver={e => { e.preventDefault(); setDragOver(true) }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <div style={{ fontSize: 24, marginBottom: 6 }}>📤</div>
        <div style={{ color: 'var(--text-2)', fontSize: 11 }}>
          {uploading ? 'Uploading...' : 'Drop files here or click to upload'}
        </div>
      </div>
      <input ref={inputRef} type="file" multiple hidden onChange={e => {
        for (const file of Array.from(e.target.files || [])) uploadFile(file)
        e.target.value = ''
      }} />

      {/* File list */}
      {loading ? <div style={{ color: 'var(--text-2)' }}>Loading...</div> : (
        files.length === 0 ? <div style={{ color: 'var(--text-2)' }}>No files uploaded yet.</div> : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {files.map(f => (
              <div key={f.id} style={{ background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
                {isImage(f.mimetype) ? (
                  <img src={f.url} alt={f.filename} style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
                ) : (
                  <div style={{ width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg-3)', borderRadius: 4, fontSize: 16 }}>📄</div>
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.filename}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-2)' }}>{formatSize(f.size)} · {new Date(f.created_at).toLocaleDateString()}</div>
                </div>
                <button onClick={() => copyUrl(f.url)} title="Copy URL" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4 }}>📋</button>
                <button onClick={() => handleDelete(f.id)} title="Delete" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4 }}>🗑️</button>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}
